import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { lang } = useLanguage();
  const cart = useCart();
  const [state, setState] = useState({ loading: true, status: null, error: null });
  const polledRef = useRef(false);

  const tDe = lang === "de";
  const T = {
    loading: tDe ? "Zahlung wird geprüft..." : "Verifying payment...",
    paid_title: tDe ? "Zahlung erfolgreich!" : "Payment successful!",
    paid_text: tDe
      ? "Vielen Dank für deine Bestellung. Du erhältst gleich eine Bestätigung per E-Mail."
      : "Thanks for your order. You'll get a confirmation email shortly.",
    pending_title: tDe ? "Zahlung wird verarbeitet" : "Processing payment",
    pending_text: tDe ? "Bitte warte einen Moment..." : "Please wait a moment...",
    failed_title: tDe ? "Zahlung nicht erfolgreich" : "Payment not successful",
    failed_text: tDe ? "Bitte versuche es erneut." : "Please try again.",
    back: tDe ? "Zurück zur Startseite" : "Back to home",
    summary: tDe ? "Bestellung" : "Order",
    total: tDe ? "Gesamt" : "Total",
    size: tDe ? "Größe" : "Size",
  };

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, status: null, error: "missing_session" });
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ loading: false, status: data, error: null });
          cart.clear();
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, status: data, error: "expired" });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ loading: false, status: data, error: "timeout" });
          return;
        }
        attempts += 1;
        setTimeout(poll, interval);
      } catch (err) {
        console.error(err);
        setState({ loading: false, status: null, error: "fetch_failed" });
      }
    };

    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fmt = (n) => `CHF ${Number(n).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-32" data-testid="checkout-success">
      <div className="max-w-xl w-full">
        {state.loading && (
          <div className="text-center" data-testid="status-loading">
            <Loader2 className="w-10 h-10 text-[#00A651] mx-auto animate-spin mb-6" />
            <p className="text-white/70 font-condensed uppercase tracking-widest text-sm">{T.loading}</p>
          </div>
        )}

        {!state.loading && state.status?.payment_status === "paid" && (
          <div data-testid="status-paid">
            <CheckCircle2 className="w-14 h-14 text-[#00A651] mb-6" />
            <h1 className="font-brush text-5xl md:text-6xl text-white leading-none mb-4">
              {T.paid_title}
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mb-10">{T.paid_text}</p>
            <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-8">
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40 mb-4">
                {T.summary}
              </div>
              <div className="space-y-3">
                {state.status.line_items?.map((li, idx) => (
                  <div key={`${li.slug || li.name}-${li.size || "no-size"}-${idx}`} className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-white">{li.name}</span>
                      {li.size && <span className="text-white/50 ml-2 text-xs">· {T.size} {li.size}</span>}
                      <span className="text-white/40 ml-2 text-xs">× {li.quantity}</span>
                    </div>
                    <span className="text-white/70 font-display">{fmt(li.line_total_chf)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-4 pt-4 flex justify-between items-center">
                <span className="font-condensed uppercase tracking-widest text-xs text-white/60">{T.total}</span>
                <span className="font-display text-2xl text-[#00A651]">{fmt(state.status.amount_chf)}</span>
              </div>
            </div>
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00A651] hover:text-[#00A651] text-white px-6 py-3 font-condensed uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-3 h-3" /> {T.back}
            </Link>
          </div>
        )}

        {!state.loading && state.error && (
          <div data-testid="status-error" className="text-center">
            <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="font-brush text-4xl text-white mb-3">{T.failed_title}</h1>
            <p className="text-white/60 mb-8">{T.failed_text}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00A651] hover:text-[#00A651] text-white px-6 py-3 font-condensed uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-3 h-3" /> {T.back}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
